import { DateTime } from "luxon";
import { useMemo } from "react";

export type VisibleCalendarRange = { start: string; end: string } | null;

interface FleetCalendarParamsArgs {
  isEditable: boolean;
  showAllEvents: boolean;
  selectedRowId?: number;
  stableAllFleetData?: { id: number }[];
  wideRangeForAdmin: { start: string; end: string };
  bookingRange: { start: DateTime; end: DateTime };
  /** When set, used as the date range for admin all-events calendar fetch (visible month/week). Refetch when calendar view changes. */
  visibleCalendarRange?: VisibleCalendarRange;
}

export function useFleetCalendarParams({
  isEditable,
  showAllEvents,
  selectedRowId,
  stableAllFleetData,
  wideRangeForAdmin,
  bookingRange,
  visibleCalendarRange = null
}: FleetCalendarParamsArgs) {
  /** All-fleet mode: fetch by vehicleIds + date range. Admin uses visible calendar range when set, else fallback; booking uses bookingRange. */
  const calendarRangeParams = useMemo(() => {
    if (!showAllEvents || !stableAllFleetData?.length) {
      return null;
    }

    const adminRange =
      visibleCalendarRange?.start != null && visibleCalendarRange?.end != null
        ? visibleCalendarRange
        : wideRangeForAdmin;

    return {
      vehicleIds: stableAllFleetData.map(r => r.id),
      startDate: isEditable ? adminRange.start : bookingRange.start.toISODate() ?? "",
      endDate: isEditable ? adminRange.end : bookingRange.end.toISODate() ?? ""
    };
  }, [
    isEditable,
    showAllEvents,
    stableAllFleetData,
    wideRangeForAdmin,
    visibleCalendarRange,
    bookingRange
  ]);

  /** Single-vehicle: admin uses visible calendar range when set (from page loading), else no date range; booking mode uses bookingRange. */
  const singleVehicleEventsParams = useMemo(() => {
    if (showAllEvents || selectedRowId == null) {
      return null;
    }

    if (isEditable) {
      const range =
        visibleCalendarRange?.start != null && visibleCalendarRange?.end != null
          ? visibleCalendarRange
          : null;
      return range
        ? {
            vehicleId: selectedRowId,
            startDate: range.start,
            endDate: range.end
          }
        : { vehicleId: selectedRowId };
    }

    return {
      vehicleId: selectedRowId,
      startDate: bookingRange.start.toISODate() ?? undefined,
      endDate: bookingRange.end.toISODate() ?? undefined
    };
  }, [
    showAllEvents,
    selectedRowId,
    isEditable,
    visibleCalendarRange,
    bookingRange.start,
    bookingRange.end
  ]);

  return {
    calendarRangeParams,
    singleVehicleEventsParams
  };
}
